import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

function AttendanceSheet({ userInfo }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState(null);
  const [timeRecords, setTimeRecords] = useState([]);
  const [formattedRecords, setFormattedRecords] = useState({});
  const [shifts, setShifts] = useState([]);
  const [formattedShifts, setFormattedShifts] = useState({});
  const [summary, setSummary] = useState({
    workDays: 0,
    absentDays: 0,
    lateDays: 0,
    earlyLeaveDays: 0,
    totalWorkHours: 0,
    totalOvertimeHours: 0,
    totalNightOvertimeHours: 0
  });

  const navigate = useNavigate();
  const printRef = useRef();

  useEffect(() => {
    // 管理者権限チェック
    if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
      navigate('/time-clock');
      return;
    }

    fetchUsers();
  }, [userInfo, navigate]);

  useEffect(() => {
    if (selectedUser) {
      fetchAttendanceData();
      fetchShifts();
    }
  }, [selectedUser, selectedMonth]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/users', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('ユーザー一覧の取得に失敗しました');
      }

      const data = await response.json();
      setUsers(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      
      // 選択された年月をフォーマット
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const formattedMonth = `${year}-${String(month).padStart(2, '0')}`;
      
      // 勤怠記録を取得
      const response = await fetch(`/api/admin/users/${selectedUser.id}/time-records?month=${formattedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('勤怠記録の取得に失敗しました');
      }

      const data = await response.json();
      console.log('取得した勤怠記録:', data); // デバッグ用
      setTimeRecords(data);
      
      // 日付ごとにデータを整形
      const formattedData = {};
      
      data.forEach(record => {
        if (!record || !record.recorded_at) return;
        
        // 日付文字列を生成
        const recordDate = new Date(record.recorded_at);
        const dateStr = formatDate(recordDate);
        console.log(`レコードID: ${record.id}, 日付: ${dateStr}`);
        
        if (!formattedData[dateStr]) {
          formattedData[dateStr] = {
            date: dateStr,
            records: []
          };
        }
        
        formattedData[dateStr].records.push(record);
      });
      
      // 各日付の出勤・退勤時間を特定
      Object.keys(formattedData).forEach(dateStr => {
        const dayRecords = formattedData[dateStr].records;
        let clockIn = null;
        let clockOut = null;
        let note = '';
        let breakTime = null;
        let hasNormalRecords = false; // 通常の打刻記録があるかどうかのフラグ
        
        console.log(`日付 ${dateStr} の処理開始 - レコード数: ${dayRecords.length}`);
        
        // まず通常の打刻記録があるか確認
        dayRecords.forEach(record => {
          if (record.record_type === 'clock_in' || record.record_type === 'in' ||
              record.record_type === 'clock_out' || record.record_type === 'out') {
            hasNormalRecords = true;
          }
        });
        
        console.log(`日付 ${dateStr} - 通常の打刻記録あり: ${hasNormalRecords}`);
        
        dayRecords.forEach(record => {
          console.log(`日付 ${dateStr} - レコードID: ${record.id}, タイプ: ${record.record_type}, 記録時間: ${record.recorded_at}`);
          
          // 欠勤記録の場合（通常の打刻記録がない場合のみ処理）
          if (record.record_type === 'absence' && !hasNormalRecords) {
            // 備考欄をそのまま使用（「欠勤: 」のプレフィックスが既に含まれている場合がある）
            note = record.note || '欠勤';
            // 「欠勤: 」のプレフィックスがない場合は追加
            if (!note.startsWith('欠勤:') && !note.startsWith('遅刻:') && !note.startsWith('早退:')) {
              note = `欠勤: ${note}`;
            }
            breakTime = null; // 欠勤の場合は休憩時間をnullに設定
            console.log(`日付 ${dateStr} - 欠勤記録を処理: ${note}`);
            return;
          }
          
          // record_typeが 'clock_in' または 'in' の場合
          if (record.record_type === 'clock_in' || record.record_type === 'in') {
            const timeStr = formatTime(new Date(record.recorded_at));
            console.log('出勤記録ID:', record.id);
            console.log('出勤記録時間:', timeStr);
            
            if (!clockIn || timeStr < clockIn) {
              clockIn = timeStr;
              console.log(`日付 ${dateStr} - 出勤時間を更新: ${clockIn}`);
            }
          } 
          // record_typeが 'clock_out' または 'out' の場合
          else if (record.record_type === 'clock_out' || record.record_type === 'out') {
            const timeStr = formatTime(new Date(record.recorded_at));
            console.log('退勤記録ID:', record.id);
            console.log('退勤記録時間:', timeStr);
            
            if (!clockOut || timeStr > clockOut) {
              clockOut = timeStr;
              console.log(`日付 ${dateStr} - 退勤時間を更新: ${clockOut}`);
            }
          }
          
          if (record.note && (record.record_type === 'clock_in' || record.record_type === 'in' || 
                             record.record_type === 'clock_out' || record.record_type === 'out')) {
            note = record.note;
          }

          // 休憩時間の処理 - 最新の値を優先（修正された値を使用）
          const allRecords = dayRecords.filter(r => r.record_type === 'clock_in' || r.record_type === 'in' || r.record_type === 'clock_out' || r.record_type === 'out');
          const latestModifiedRecord = allRecords
            .filter(record => record.break_time !== null && record.break_time !== undefined)
            .sort((a, b) => {
              const aTime = new Date(a.last_modified_at || a.recorded_at).getTime();
              const bTime = new Date(b.last_modified_at || b.recorded_at).getTime();
              return bTime - aTime; // 新しい順
            })[0];
          
          if (latestModifiedRecord) {
            breakTime = latestModifiedRecord.break_time;
          } else {
            breakTime = null;
          }
        });
        
        formattedData[dateStr].clock_in = clockIn;
        formattedData[dateStr].clock_out = clockOut;
        formattedData[dateStr].note = note;
        formattedData[dateStr].break_time = breakTime;
        formattedData[dateStr].id = dayRecords[0]?.id; // 最初のレコードIDを使用
      });
      
      console.log('整形した勤怠記録:', formattedData); // デバッグ用
      setFormattedRecords(formattedData);
      
      // シフト情報を取得
      await fetchShifts();
      
      // 勤怠データを作成
      const dailyRecords = [];
      const daysInMonth = getDaysInMonth(year, month);
      
      console.log('日次データを作成中:', { year, month, daysInMonth });
      console.log('整形済み勤怠データ:', formattedData);
      console.log('整形済みシフトデータ:', formattedShifts);
      
      for (let day = 1; day <= daysInMonth; day++) {
        const isoDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const date = new Date(isoDateStr + 'T00:00:00'); // タイムゾーンに依存しない日付生成
        const dateStr = formatDate(date);
        const record = formattedData[dateStr] || {};
        const shift = formattedShifts[isoDateStr] || {};
        
        console.log(`日付 ${day}: isoDateStr=${isoDateStr}, dateStr=${dateStr}, record=`, record, 'shift=', shift);
        
        // シフトと打刻時間を比較して表示する時間を決定
        let displayClockIn = null;
        if (record.clock_in) {
          displayClockIn = determineDisplayTime(record.clock_in, shift.startTime);
        }
        
        // 退勤時間は15分刻みに切り捨てる
        let displayClockOut = null;
        if (record.clock_out) {
          displayClockOut = floorTimeToQuarter(record.clock_out);
        }
        
        // 実労働時間を計算
        let actualWorkHours = null;
        let overtimeHours = null;
        let nightOvertimeHours = null;
        
        if (displayClockIn && displayClockOut) {
          // 表示用の時間（切り上げ・切り捨て処理後）を使用して計算
          const clockInTime = parseTimeString(displayClockIn);
          const clockOutTime = parseTimeString(displayClockOut);
          
          // 休憩時間（分）
          const breakTime = record.break_time || 0;
          
          // 実労働時間（時間）= (退勤時間 - 出勤時間 - 休憩時間) / 60 / 60
          const workTimeMinutes = (clockOutTime - clockInTime) / 60000 - breakTime;
          actualWorkHours = Math.max(0, workTimeMinutes / 60);
          
          // 標準労働時間（8時間と仮定）
          const standardWorkHours = 8;
          
          // 残業時間 = 実労働時間 - 標準労働時間（負の場合は0）
          overtimeHours = Math.max(0, actualWorkHours - standardWorkHours);
          
          // 深夜残業時間（22時以降の勤務時間）- 簡易計算
          const nightStartHour = 22; // 22:00
          const clockOutHour = new Date(clockOutTime).getHours();
          
          if (clockOutHour >= nightStartHour || clockOutHour < 5) {
            // 簡易的な深夜残業時間計算
            nightOvertimeHours = Math.min(overtimeHours, 
              clockOutHour >= nightStartHour ? 
                clockOutHour - nightStartHour : 
                clockOutHour + (24 - nightStartHour));
          } else {
            nightOvertimeHours = 0;
          }
        }
        
        // 備考欄の処理
        let formattedNote = record.note || '';
        // 「欠勤: 」のプレフィックスがない場合で、内容がある場合は追加
        if (formattedNote && !formattedNote.startsWith('欠勤:') && !formattedNote.startsWith('遅刻:') && !formattedNote.startsWith('早退:')) {
          formattedNote = `備考: ${formattedNote}`;
        }
        
        dailyRecords.push({
          date: date.toISOString(),
          clockIn: displayClockIn ? new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${displayClockIn}`).toISOString() : null,
          clockOut: displayClockOut ? new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${displayClockOut}`).toISOString() : null,
          breakTime: record.break_time,
          note: formattedNote,
          actualWorkHours,
          overtimeHours,
          nightOvertimeHours,
          shiftStartTime: shift.startTime,
          shiftEndTime: shift.endTime
        });
      }
      
      // 集計情報を計算
      const calculatedSummary = calculateSummary({ dailyRecords });
      
      console.log('作成された日次レコード:', dailyRecords);
      console.log('計算された集計情報:', calculatedSummary);
      
      // 勤怠データをセット
      setAttendanceData({
        dailyRecords,
        summary: calculatedSummary
      });
      
      setSummary(calculatedSummary);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setError('勤怠データの取得に失敗しました: ' + err.message);
      setLoading(false);
    }
  };

  // シフト情報を取得
  const fetchShifts = async () => {
    try {
      // 選択された年月をフォーマット
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const formattedMonth = `${year}-${String(month).padStart(2, '0')}`;
      
      const response = await fetch(`/api/admin/users/${selectedUser.id}/shifts?month=${formattedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('シフト情報の取得に失敗しました');
      }

      const data = await response.json();
      console.log('取得したシフト情報:', data); // デバッグ用
      setShifts(data);
      
      // 日付ごとにデータを整形
      const formattedData = {};
      
      data.forEach(shift => {
        const dateStr = shift.date.split('T')[0];
        
        formattedData[dateStr] = {
          startTime: shift.start_time,
          endTime: shift.end_time,
          is_workday: shift.is_workday
        };
      });
      
      setFormattedShifts(formattedData);
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setError('シフト情報の取得に失敗しました: ' + err.message);
    }
  };

  // 時間文字列をミリ秒に変換する関数
  const parseTimeString = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date.getTime();
  };

  // 集計情報を計算
  const calculateSummary = (data) => {
    if (!data || !data.dailyRecords) return summary;

    const calculatedSummary = {
      workDays: 0,
      absentDays: 0,
      lateDays: 0,
      earlyLeaveDays: 0,
      totalWorkHours: 0,
      totalOvertimeHours: 0,
      totalNightOvertimeHours: 0
    };

    data.dailyRecords.forEach(record => {
      // 出勤日数をカウント
      if (record.clockIn && record.clockOut) {
        calculatedSummary.workDays++;
        
        // 実労働時間を加算
        if (record.actualWorkHours) {
          calculatedSummary.totalWorkHours += record.actualWorkHours;
        }
        
        // 残業時間を加算
        if (record.overtimeHours) {
          calculatedSummary.totalOvertimeHours += record.overtimeHours;
        }
        
        // 深夜残業時間を加算
        if (record.nightOvertimeHours) {
          calculatedSummary.totalNightOvertimeHours += record.nightOvertimeHours;
        }
        
        // 遅刻日数をカウント（9:00以降を遅刻と仮定）
        if (record.clockIn) {
          const clockInTime = new Date(record.clockIn);
          const startHour = clockInTime.getHours();
          const startMinute = clockInTime.getMinutes();
          
          if (startHour > 9 || (startHour === 9 && startMinute > 0)) {
            calculatedSummary.lateDays++;
          }
        }
        
        // 早退日数をカウント（17:00前を早退と仮定）
        if (record.clockOut) {
          const clockOutTime = new Date(record.clockOut);
          const endHour = clockOutTime.getHours();
          const endMinute = clockOutTime.getMinutes();
          
          if (endHour < 17 || (endHour === 17 && endMinute < 0)) {
            calculatedSummary.earlyLeaveDays++;
          }
        }
      } 
      // 欠勤日数をカウント（土日を除く）
      else {
        const recordDate = new Date(record.date);
        const dayOfWeek = recordDate.getDay();
        
        // 土曜日(6)と日曜日(0)を除く
        if (dayOfWeek !== 0 && dayOfWeek !== 6 && record.note === '欠勤') {
          calculatedSummary.absentDays++;
        }
      }
    });

    return calculatedSummary;
  };

  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split('-').map(Number);
    setSelectedMonth(new Date(year, month - 1, 1));
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    if (!userId) {
      setSelectedUser(null);
      setAttendanceData(null);
      return;
    }
    
    const user = users.find(u => u.id.toString() === userId.toString());
    setSelectedUser(user);
  };

  // 印刷機能
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `勤務表_${selectedMonth.getFullYear()}年${selectedMonth.getMonth() + 1}月_${selectedUser?.name || 'all'}`,
    onAfterPrint: () => console.log('印刷が完了しました')
  });

  // PDF出力機能
  const handleExportPDF = async () => {
    try {
      if (!printRef.current) return;
      
      const content = printRef.current;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // マージン
      
      // コンテンツの幅と高さを取得
      const canvas = await html2canvas(content, {
        scale: 1.5, // 解像度を調整
        useCORS: true, // 外部リソースを許可
        logging: false, // ログを無効化
        allowTaint: true // タイント（汚染）を許可
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pdfWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const availableHeight = pdfHeight - (margin * 2);
      
      // 1ページに収まるようスケールを調整
      let finalImgWidth = imgWidth;
      let finalImgHeight = imgHeight;
      
      if (imgHeight > availableHeight) {
        // 高さがページを超える場合、高さに合わせてスケール調整
        const scaleFactor = availableHeight / imgHeight;
        finalImgWidth = imgWidth * scaleFactor;
        finalImgHeight = availableHeight;
      }
      
      // 画像を1ページに配置
      const xOffset = (pdfWidth - finalImgWidth) / 2; // 中央寄せ
      pdf.addImage(imgData, 'PNG', xOffset, margin, finalImgWidth, finalImgHeight);
      
      // PDFをダウンロード
      const userName = selectedUser ? selectedUser.name : 'all';
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      pdf.save(`勤務表_${year}年${month}月_${userName}.pdf`);
      
    } catch (error) {
      console.error('PDF生成エラー:', error);
      alert('PDFの生成中にエラーが発生しました。');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit', weekday: 'short' });
  };

  const formatHours = (hours) => {
    if (hours === undefined || hours === null) return '-';
    
    // 小数点の時間を時間と分に変換
    const totalMinutes = Math.round(hours * 60);
    const hoursPart = Math.floor(totalMinutes / 60);
    const minutesPart = totalMinutes % 60;
    
    // 時間:分 形式で返す
    return `${hoursPart}:${String(minutesPart).padStart(2, '0')}`;
  };

  const formatBreakTime = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  // 時間を15分刻みに切り上げる関数
  const roundTimeToQuarter = (timeStr) => {
    if (!timeStr) return null;
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    let roundedMinutes = Math.ceil(minutes / 15) * 15;
    let roundedHours = hours;
    
    if (roundedMinutes === 60) {
      roundedMinutes = 0;
      roundedHours += 1;
    }
    
    return `${String(roundedHours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
  };
  
  // 時間を15分刻みに切り捨てる関数
  const floorTimeToQuarter = (timeStr) => {
    if (!timeStr) return null;
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    let flooredMinutes = Math.floor(minutes / 15) * 15;
    let flooredHours = hours;
    
    return `${String(flooredHours).padStart(2, '0')}:${String(flooredMinutes).padStart(2, '0')}`;
  };
  
  // シフトと打刻時間を比較して表示する時間を決定する関数
  const determineDisplayTime = (clockInTime, shiftStartTime) => {
    if (!clockInTime) return null;
    if (!shiftStartTime) return roundTimeToQuarter(clockInTime);
    
    // 時間を分に変換して比較
    const [clockInHours, clockInMinutes] = clockInTime.split(':').map(Number);
    const [shiftHours, shiftMinutes] = shiftStartTime.split(':').map(Number);
    
    const clockInTotalMinutes = clockInHours * 60 + clockInMinutes;
    const shiftTotalMinutes = shiftHours * 60 + shiftMinutes;
    
    // シフト開始時間より前に打刻した場合はシフト開始時間を返す
    if (clockInTotalMinutes < shiftTotalMinutes) {
      return shiftStartTime;
    }
    
    // シフト開始時間より後に打刻した場合は実際の打刻時間を15分刻みに切り上げて返す
    return roundTimeToQuarter(clockInTime);
  };

  if (loading && !attendanceData) {
    return <div className="flex justify-center items-center h-screen">読み込み中...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  // 月の日数を取得
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // 選択された月の日数
  const daysInMonth = getDaysInMonth(
    selectedMonth.getFullYear(),
    selectedMonth.getMonth() + 1
  );

  // 日付の配列を生成
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">勤務表作成</h1>
      
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-700 mb-1">社員選択</label>
          <select
            className="w-full md:w-64 p-2 border border-gray-300 rounded-md"
            onChange={handleUserChange}
            value={selectedUser?.id || ''}
          >
            <option value="">社員を選択してください</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </div>
        
        <div className="w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-700 mb-1">年月選択</label>
          <input
            type="month"
            className="w-full md:w-64 p-2 border border-gray-300 rounded-md"
            value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`}
            onChange={handleMonthChange}
          />
        </div>
      </div>
      
      {selectedUser && attendanceData && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            onClick={handlePrint}
            className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded"
          >
            印刷
          </button>
          <button
            onClick={handleExportPDF}
            className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded"
          >
            PDF出力
          </button>
        </div>
      )}
      
      {selectedUser && attendanceData && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-5 mb-8 mx-auto max-w-6xl" ref={printRef}>
          <div className="attendance-sheet">
            {/* ヘッダー部分 */}
            <div className="mb-5 attendance-header">
              {/* ロゴと勤務表タイトルを配置 */}
              <div className="flex justify-between items-center mb-4 header-top">
                <div className="w-1/5">
                  <img 
                    src="/api/company-logo/COMPANY1" 
                    alt="会社ロゴ" 
                    className="max-h-10 max-w-full"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCAyMDAgNTAiPjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iNTAiIGZpbGw9IiNlZWUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE4IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiBmaWxsPSIjOTk5Ij5Db21wYW55IExvZ288L3RleHQ+PC9zdmc+';
                    }}
                  />
                </div>
                <div className="text-center flex-1 title-section">
                  <h2 className="text-2xl font-bold mb-1">勤務表</h2>
                  <p className="text-lg">{selectedMonth.getFullYear()} 年 {selectedMonth.getMonth() + 1} 月</p>
                </div>
                <div className="w-1/5"></div> {/* 右側のスペース確保 */}
              </div>
              
              <div className="employee-info">
                <div className="border-b-2 border-gray-800 pb-2">
                  <p className="text-lg font-semibold">社員名: {selectedUser.name}</p>
                </div>
              </div>
            </div>
            
            {/* 集計情報 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="border border-gray-200 rounded">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">出勤日数</th>
                      <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">欠勤日数</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-3 py-2 text-center text-gray-800 font-semibold text-lg numeric-cell">{summary.workDays} 日</td>
                      <td className="px-3 py-2 text-center text-gray-800 font-semibold text-lg numeric-cell">{summary.absentDays} 日</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="border border-gray-200 rounded">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">遅刻日数</th>
                      <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">早退日数</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-3 py-2 text-center text-gray-800 font-semibold text-lg numeric-cell">{summary.lateDays} 日</td>
                      <td className="px-3 py-2 text-center text-gray-800 font-semibold text-lg numeric-cell">{summary.earlyLeaveDays} 日</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded mb-4">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">総労働時間</th>
                    <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">総残業時間</th>
                    <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">総深夜残業時間</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 text-center text-gray-800 font-semibold text-lg numeric-cell">{formatHours(summary.totalWorkHours)}</td>
                    <td className="px-3 py-2 text-center text-gray-800 font-semibold text-lg numeric-cell">{formatHours(summary.totalOvertimeHours)}</td>
                    <td className="px-3 py-2 text-center text-gray-800 font-semibold text-lg numeric-cell">{formatHours(summary.totalNightOvertimeHours)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* 勤務データテーブル */}
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-300 attendance-data-table">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">日付</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">出勤時間</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">退勤時間</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">休憩時間</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">実労働時間</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">残業時間</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">深夜残業</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700 w-1/4">備考</th>
                  </tr>
                </thead>
                <tbody>
                  {daysArray.map(day => {
                    const dateStr = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const date = new Date(dateStr + 'T00:00:00'); // タイムゾーンに依存しない日付生成
                    const record = attendanceData.dailyRecords.find(r => {
                      if (!r || !r.date) return false;
                      const recordDate = new Date(r.date);
                      const recordDateStr = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}-${String(recordDate.getDate()).padStart(2, '0')}`;
                      return recordDateStr === dateStr;
                    }) || {};
                    
                    // 土日の場合は背景色を変える
                    const dayOfWeek = date.getDay();
                    const isSaturday = dayOfWeek === 6;
                    const isSunday = dayOfWeek === 0;
                    let bgColorClass = '';
                    
                    if (isSunday) {
                      bgColorClass = 'bg-red-50'; // 日曜日は淡い赤色
                    } else if (isSaturday) {
                      bgColorClass = 'bg-blue-50'; // 土曜日は淡い青色
                    }
                    
                    return (
                      <tr key={day} className={bgColorClass}>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm">{formatDate(date)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm numeric-cell">{formatTime(record.clockIn)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm numeric-cell">{formatTime(record.clockOut)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm numeric-cell">{record.breakTime ? formatBreakTime(record.breakTime) : '-'}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm numeric-cell">{formatHours(record.actualWorkHours)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm numeric-cell">{formatHours(record.overtimeHours)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm numeric-cell">{formatHours(record.nightOvertimeHours)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm whitespace-normal break-words">{record.note || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AttendanceSheet; 